/**
 * Encoding + delimiter sniffing for delimited-text intake. Operates on raw
 * bytes so it can repair the common real-world case (a CSV exported as
 * Windows-1252/Latin-1 that isn't valid UTF-8) before parsing. Deep
 * double-encoding ("mojibake") repair is best-effort and out of v1 scope; the
 * goal is "don't hand the parser garbage or a UTF-8 replacement soup."
 */

export type Delimiter = "," | "\t" | ";" | "|";
const DELIMITERS: Delimiter[] = [",", "\t", ";", "|"];

/** Strip a leading UTF-8/UTF-16 BOM from an already-decoded string. */
export function stripBom(text: string): string {
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
}

export interface DecodedText {
  text: string;
  encoding: "utf-8" | "windows-1252";
}

/**
 * Decode bytes to text. Honors a UTF-8 BOM; otherwise decodes UTF-8 and, if the
 * result contains the U+FFFD replacement char (i.e. the bytes weren't valid
 * UTF-8), falls back to Windows-1252 which maps every byte losslessly.
 */
export function decodeBytes(bytes: Uint8Array): DecodedText {
  if (bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return { text: stripBom(new TextDecoder("utf-8").decode(bytes)), encoding: "utf-8" };
  }
  const utf8 = new TextDecoder("utf-8").decode(bytes);
  if (!utf8.includes("�")) return { text: stripBom(utf8), encoding: "utf-8" };
  return { text: stripBom(new TextDecoder("windows-1252").decode(bytes)), encoding: "windows-1252" };
}

/**
 * Detect the delimiter from a text sample. For each candidate, score by how
 * consistent its per-line count is across the first data lines (a real
 * delimiter appears the same number of times on every row); ties break toward
 * the higher count. Defaults to comma when there's no signal.
 */
export function detectDelimiter(sample: string): Delimiter {
  const lines = sample.split(/\r?\n/).filter((l) => l.trim() !== "").slice(0, 10);
  if (lines.length === 0) return ",";

  let best: Delimiter = ",";
  let bestScore = -1;
  for (const d of DELIMITERS) {
    const counts = lines.map((l) => l.split(d).length - 1);
    const total = counts.reduce((a, b) => a + b, 0);
    if (total === 0) continue;
    const mean = total / counts.length;
    const variance = counts.reduce((a, c) => a + (c - mean) ** 2, 0) / counts.length;
    // Reward high, consistent counts: mean divided by (1 + variance).
    const score = mean / (1 + variance);
    if (score > bestScore) {
      bestScore = score;
      best = d;
    }
  }
  return best;
}
